import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getWebSocketUrl } from '../config/environment';
import type { TeamChatMessage } from '../services/teamChatService';

export interface TeamChatInboxEvent {
  conversationId: string;
  message?: TeamChatMessage;
  reason: 'message' | 'message_updated' | 'created' | 'updated';
  conversationType?: 'direct' | 'group' | null;
  mutedUserIds?: string[];
}

export interface TeamChatTypingEvent {
  conversationId: string;
  userId: string;
  userName?: string;
  isTyping: boolean;
}

export interface TeamChatReadEvent {
  conversationId: string;
  userId: string;
  readAt: string;
}

interface UseTeamChatRealtimeOptions {
  /** Conversa aberta — o hook entra/sai da sala automaticamente. */
  activeConversationId?: string | null;
  /** Conversas abertas simultaneamente no dock flutuante. */
  activeConversationIds?: string[];
  onInbox?: (event: TeamChatInboxEvent) => void;
  onMessage?: (conversationId: string, message: TeamChatMessage) => void;
  onMessageUpdated?: (conversationId: string, message: TeamChatMessage) => void;
  onTyping?: (event: TeamChatTypingEvent) => void;
  onRead?: (event: TeamChatReadEvent) => void;
  enabled?: boolean;
}

const readToken = (): string | null =>
  localStorage.getItem('token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');

/**
 * Conexão WebSocket do chat interno: entrega imediata de mensagens, presença
 * dos colegas e indicador de digitação. Handlers ficam em refs para que o
 * socket não seja recriado a cada render do componente.
 */
export function useTeamChatRealtime(options: UseTeamChatRealtimeOptions) {
  const { activeConversationId, activeConversationIds = [], enabled = true } = options;
  const roomIds = Array.from(
    new Set([activeConversationId, ...activeConversationIds].filter(Boolean) as string[]),
  ).sort();
  const roomIdsKey = roomIds.join('|');

  const [connected, setConnected] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());

  const socketRef = useRef<Socket | null>(null);
  const handlersRef = useRef(options);
  handlersRef.current = options;

  useEffect(() => {
    if (!enabled) return undefined;

    const url = getWebSocketUrl();
    const token = readToken();
    if (!url?.trim() || !token) return undefined;

    const socket = io(url, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 15,
      auth: { token },
    });
    socketRef.current = socket;

    const requestPresence = () => socket.emit('team_chat:presence_request');

    socket.on('connect', () => {
      setConnected(true);
      requestPresence();
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('reconnect', requestPresence);

    socket.on('team_chat:presence_snapshot', (payload: { userIds?: string[] }) => {
      setOnlineUserIds(new Set(payload?.userIds || []));
    });

    socket.on('team_chat:presence', (payload: { userId?: string; online?: boolean }) => {
      if (!payload?.userId) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (payload.online) next.add(payload.userId!);
        else next.delete(payload.userId!);
        return next;
      });
    });

    socket.on('team_chat:message', (payload: { conversationId?: string; message?: TeamChatMessage }) => {
      if (!payload?.conversationId || !payload.message) return;
      handlersRef.current.onMessage?.(payload.conversationId, payload.message);
    });

    socket.on(
      'team_chat:message_updated',
      (payload: { conversationId?: string; message?: TeamChatMessage }) => {
        if (!payload?.conversationId || !payload.message) return;
        handlersRef.current.onMessageUpdated?.(payload.conversationId, payload.message);
      },
    );

    socket.on('team_chat:inbox', (payload: TeamChatInboxEvent) => {
      if (!payload?.conversationId) return;
      handlersRef.current.onInbox?.(payload);
    });

    socket.on('team_chat:typing', (payload: TeamChatTypingEvent) => {
      if (!payload?.conversationId || !payload.userId) return;
      handlersRef.current.onTyping?.(payload);
    });

    socket.on('team_chat:read', (payload: TeamChatReadEvent) => {
      if (!payload?.conversationId) return;
      handlersRef.current.onRead?.(payload);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  // Salas abertas: a página usa uma; o dock flutuante pode usar várias.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || roomIds.length === 0) return undefined;

    const join = () => roomIds.forEach((id) => socket.emit('team_chat:join', id));
    join();
    socket.on('connect', join);

    return () => {
      socket.off('connect', join);
      roomIds.forEach((id) => socket.emit('team_chat:leave', id));
    };
    // roomIdsKey estabiliza o efeito mesmo quando o chamador recria o array.
  }, [roomIdsKey, connected]);

  const emitTyping = useCallback((conversationId: string, isTyping: boolean) => {
    socketRef.current?.emit('team_chat:typing', { conversationId, isTyping });
  }, []);

  return { connected, onlineUserIds, emitTyping };
}

export default useTeamChatRealtime;
